import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { headline, subhead } from '../copy';
import type { PayerAudience, PayerState } from '../types';

export interface HeadlineProps {
  /** Fold counts; `ready` must be the confirmed-ready count, not the raw state count. */
  counts: Record<PayerState, number> | { needs_customer: number; ready: number; needs_us?: number };
  /**
   * Confirmed-ready payers (`fold.readyCount`): state `ready` with a claim
   * confirmed absent. Never the raw `counts.ready`, which includes payers whose
   * claim evidence is still unknown and would over-count the subhead.
   */
  readyCount: number;
  enrollingCount: number;
  liveCount: number;
  unknownCount?: number;
  audience?: PayerAudience;
  /** Right-aligned freshness line ("Checked with the clearinghouse at 2:17 PM"). */
  rightSlot?: ReactNode;
}

/** AC-5: the billing track's heading and the line under it, derived from fold counts. */
export function Headline({
  counts,
  readyCount,
  enrollingCount,
  liveCount,
  unknownCount = 0,
  audience,
  rightSlot,
}: HeadlineProps) {
  return (
    <Stack
      direction="row"
      spacing={2.5}
      sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          sx={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.02em', color: '#1c1c22' }}
        >
          {headline(counts, unknownCount, audience, enrollingCount)}
        </Typography>
        <Typography
          sx={{ fontSize: '13px', color: '#6b6b78', lineHeight: 1.55, mt: '4px', maxWidth: 620 }}
        >
          {subhead({
            enrollingCount,
            liveCount,
            readyCount,
            needsUsCount: counts.needs_us ?? 0,
            unknownCount,
          })}
        </Typography>
      </Box>
      {rightSlot}
    </Stack>
  );
}
